import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Send } from "lucide-react";
import { NAV_LINKS, SITE } from "../lib/site";
import { WhatsAppCTA } from "./Buttons";
import WhatsAppIcon from "./icons/WhatsAppIcon";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [hoverServices, setHoverServices] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change.
  useEffect(() => {
    setOpen(false);
    setHoverServices(false);
  }, [location.pathname]);

  // Navbar always visible from page load.
  return (
    <header
      className="fixed left-0 right-0 top-0 z-50 bg-paper/85 backdrop-blur-md border-b border-[var(--border-light)]"
      data-testid="site-navbar"
    >
      <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-[64px]">
          {/* Brand: ICAI (CA India) logo + SVMD wordmark */}
          <Link
            to="/"
            data-testid="brand-link"
            aria-label="SVMD & Associates — Home"
            className="group flex items-center text-ink"
          >
            <img
              src="/icai-logo.png"
              alt="ICAI — Institute of Chartered Accountants of India"
              data-testid="icai-logo"
              className="h-7 md:h-8 w-auto select-none pointer-events-none"
              loading="eager"
            />
            <span
              aria-hidden
              className="mx-3 self-stretch w-px bg-[rgba(10,22,40,0.15)]"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-semibold text-[15px] tracking-[-0.01em]">
                SVMD &amp; Associates
              </span>
              <span className="text-[10px] tracking-[0.16em] uppercase text-ink/50">
                Chartered Accountants
              </span>
            </div>
            {/* Mobile compact wordmark */}
            <span className="sm:hidden font-display font-semibold text-[15px] tracking-[-0.01em]">
              SVMD &amp; Associates
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) =>
              l.children ? (
                <div
                  key={l.href}
                  className="relative"
                  onMouseEnter={() => setHoverServices(true)}
                  onMouseLeave={() => setHoverServices(false)}
                >
                  <NavLink
                    to={l.href}
                    end={false}
                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-[14px] font-medium transition-colors ${
                        isActive ? "text-ink" : "text-ink/65 hover:text-ink"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown
                      size={14}
                      strokeWidth={2}
                      className={`transition-transform ${hoverServices ? "rotate-180" : ""}`}
                    />
                  </NavLink>
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[400px] transition-all duration-200 ${
                      hoverServices
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-1 pointer-events-none"
                    }`}
                  >
                    <div className="bg-ink rounded-lg p-2 border border-white/10 shadow-2xl shadow-ink/40 relative">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 -translate-y-1 rotate-45 bg-ink border-l border-t border-white/10" />
                      {l.children.map((c) => (
                        <Link
                          key={c.href}
                          to={c.href}
                          data-testid={`nav-mega-${c.href}`}
                          className="group flex items-start gap-3 px-4 py-3 rounded-md hover:bg-white/5 transition-colors"
                        >
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-voltage shrink-0 group-hover:scale-125 transition-transform" />
                          <span className="flex-1">
                            <span className="block text-paper text-[14px] font-medium">
                              {c.label}
                            </span>
                            <span className="block text-paper/55 text-[12px] mt-0.5">
                              {c.desc}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={l.href}
                  to={l.href}
                  end={l.href === "/"}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative text-[14px] font-medium transition-colors ${
                      isActive ? "text-ink" : "text-ink/65 hover:text-ink"
                    } ${
                      isActive
                        ? "after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-2 after:h-0.5 after:bg-voltage"
                        : ""
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <a
              href={SITE.arattaiUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-arattai-link"
              aria-label="Follow us on Arattai"
              title="Follow us on Arattai"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 rounded-md border border-ink/15 text-ink/75 hover:border-voltage hover:text-ink transition-colors"
            >
              <Send size={14} strokeWidth={1.8} />
              <span>Arattai</span>
            </a>
            <WhatsAppCTA testId="nav-whatsapp-cta" label="WhatsApp" />
          </div>

          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 text-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-toggle"
          >
            {open ? <X size={22} strokeWidth={1.8} /> : <Menu size={22} strokeWidth={1.8} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 top-[64px] z-40 bg-ink transition-all duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="h-full flex flex-col px-6 pt-8 pb-8 overflow-y-auto">
          <nav className="flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <div key={l.href}>
                <NavLink
                  to={l.href}
                  end={l.href === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-[28px] leading-none tracking-tight ${
                      isActive ? "text-voltage" : "text-paper"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
                {l.children && (
                  <div className="mt-3 ml-1 flex flex-col gap-2">
                    {l.children
                      .filter((c) => c.href !== l.href)
                      .map((c) => (
                        <Link
                          key={c.href}
                          to={c.href}
                          data-testid={`mobile-nav-mega-${c.href}`}
                          className="text-paper/65 hover:text-voltage text-[14px]"
                        >
                          → {c.label}
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="mt-auto pt-8 flex flex-col gap-3">
            <a
              href={SITE.arattaiUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-arattai"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-[14px] font-medium border border-white/25 text-paper hover:border-voltage hover:text-voltage transition-colors w-full"
            >
              <Send size={16} strokeWidth={2} />
              <span>Follow us on Arattai</span>
            </a>
            <a
              href={SITE.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-whatsapp-cta"
              className="voltage-cta inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-[14px] font-medium w-full"
            >
              <WhatsAppIcon size={18} strokeWidth={2} />
              <span>Chat on WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
